import { computed, makeObservable, observable } from "mobx"

import type { IGauge } from "@/domains/gauge"
import type { GaugeStats, IEmissionsConfig } from "@/domains/gauge/interfaces"
import type { IncentiveStore } from "@/domains/incentive"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { SelectedPowerStore } from "@/domains/selected-power"
import type { TokenStore } from "@/domains/tokens"

const sanitizeNumber = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0
  }
  if (value === Infinity) {
    return 0
  }
  return value
}

// Epoch constants
const EPOCH_DURATION_SECONDS = 7 * 24 * 60 * 60 // 7 days in seconds (604800)
const MIN_ELAPSED_SECONDS = 60 // Minimum 1 minute to avoid division by zero

/**
 * Dilution Risk Rank - Strongly typed risk levels
 * Used for filtering, sorting, and UI presentation
 */
export type DilutionRiskRank = "low" | "moderate" | "high" | "critical"

/**
 * Dilution Risk Threshold Constants
 * These define the boundaries for each risk level
 */
export const DILUTION_RISK_THRESHOLDS = {
  LOW_MAX: 24, // 0-24: Low risk (green)
  MODERATE_MAX: 49, // 25-49: Moderate risk (yellow)
  HIGH_MAX: 74, // 50-74: High risk (orange)
  CRITICAL_MIN: 75, // 75-100: Critical risk (red)
} as const

/**
 * Map DilutionRiskRank to color value
 * Used for UI presentation in progress bars, filter dropdowns, etc.
 */
export const getDilutionRiskColor = (rank: DilutionRiskRank): string => {
  switch (rank) {
    case "low":
      return "rgb(34, 197, 94)" // green-500
    case "moderate":
      return "rgb(234, 179, 8)" // yellow-500
    case "high":
      return "rgb(249, 115, 22)" // orange-500
    case "critical":
      return "rgb(239, 68, 68)" // red-500
  }
}

/**
 * VotingGauge - View model that combines a gauge with vote allocation and computed stats
 *
 * This class contains all vote-dependent calculations that were previously in gauge.updateStats().
 * Gauges remain static domain entities, while VotingGauge is a view model for the vote page.
 */
export class VotingGauge {
  // Vote allocation (0-100 percentage)
  voteValue: number = 0

  constructor(
    public readonly gauge: IGauge,
    private readonly selectedPowerStore: SelectedPowerStore | undefined,
    private readonly lockLookup: ILockLookup | undefined,
    private readonly emissionsConfig: IEmissionsConfig | undefined,
    private readonly now: Date = new Date(), // For testing - defaults to current UTC time
  ) {
    makeObservable(this, {
      voteValue: observable,
      stats: computed,
      isInSuperVoter: computed,
      // Facade properties for View components
      id: computed,
      displayName: computed,
      protocol: computed,
      apr: computed,
      perVote: computed,
      expectedIncome: computed,
      fees: computed,
      emissions: computed,
      tokens: computed,
      allocation: computed,
      votes: computed,
      votesPercent: computed,
      tvlUsd: computed,
      dilutionRisk: computed,
      dilutionRiskRank: computed,
      dilutionRiskColor: computed,
      efficiency: computed,
      incentives: computed,
      lastEpochVoteCount: computed,
    })
  }

  /**
   * Computed: Is this gauge in SuperVoter?
   * A gauge is in SuperVoter if it has a vote allocation > 0
   */
  get isInSuperVoter(): boolean {
    return this.voteValue > 0
  }

  /**
   * Get selected vote power from store
   */
  private get selectedVotePower(): number {
    return this.selectedPowerStore?.selectedVotePower ?? 0
  }

  /**
   * Get selected lock IDs from store
   */
  private get selectedLockIds(): number[] {
    return this.selectedPowerStore?.selectedNftIds ?? []
  }

  /**
   * Calculate how many seconds have elapsed in the current epoch
   * Epochs run from Thursday 00:00:00 UTC to Wednesday 23:59:59 UTC
   */
  private getTimeElapsedInEpoch(now: Date): number {
    // Get seconds since epoch (Unix timestamp)
    const secondsSinceEpoch = Math.floor(now.getTime() / 1000)
    // Get seconds elapsed in current epoch (modulo epoch duration)
    const elapsed = secondsSinceEpoch % EPOCH_DURATION_SECONDS
    // Return at least MIN_ELAPSED_SECONDS to avoid division by zero
    return Math.max(elapsed, MIN_ELAPSED_SECONDS)
  }

  /**
   * Computed stats - all vote-dependent calculations
   * Moved from gauge.updateStats() to here to avoid side effects on domain entities
   */
  get stats(): GaugeStats {
    if (!this.emissionsConfig || !this.lockLookup) {
      // Return default stats if dependencies not available
      return {
        totalVotes: this.gauge.votes,
        bribesPlusFees: Number(this.gauge.totalBribeValueUsd),
        incomePerVote: 0,
        myVoteValue: this.voteValue,
        myAvailableVotes: this.selectedVotePower,
        myVoteCount: 0,
        newInclusiveVoteCount: this.gauge.votes,
        newInclusiveIncomePerVote: 0,
        newExpectedIncome: 0,
        incomeEfficiency: 0,
        nextEmissions: 0,
      }
    }

    // Query locks for last epoch votes
    const selectedLockIds = this.selectedLockIds
    const selectedLocks = selectedLockIds
      .map((id) => this.lockLookup!.find(id))
      .filter((lock): lock is NonNullable<typeof lock> => lock !== undefined)

    // Sum up last epoch votes for this gauge from all selected locks
    const totalLastEpochVotes = selectedLocks.reduce((sum, lock) => {
      const vote = lock.getLastEpochVoteForGauge(this.gauge.revenueSourceAddress)
      return sum + (vote?.balance ?? 0)
    }, 0)

    const myPreviousVoteCount = sanitizeNumber(totalLastEpochVotes)

    const totalGaugeVotes = this.gauge.votes
    const bribesPlusFees = Number(this.gauge.totalBribeValueUsd)
    const incomePerVote = sanitizeNumber(bribesPlusFees / totalGaugeVotes)

    const myCurrentVoteValue = Number(this.voteValue)

    const myVoteCount = myCurrentVoteValue > 0 ? this.selectedVotePower * (myCurrentVoteValue / 100) : 0
    const newInclusiveVoteCount = totalGaugeVotes + myVoteCount - myPreviousVoteCount
    const newInclusiveIncomePerVote = sanitizeNumber(bribesPlusFees / newInclusiveVoteCount)
    const newExpectedIncome = sanitizeNumber(newInclusiveIncomePerVote * myVoteCount)
    const incomeEfficiency = sanitizeNumber(incomePerVote / bribesPlusFees)

    // Calculate next emissions
    let nextEmissions =
      this.gauge.votesPercent *
      this.emissionsConfig.emitted *
      this.emissionsConfig.decayFactor ** (Math.floor(Date.now() / 604800e3) - this.emissionsConfig.universalEpoch)

    nextEmissions = Number.isNaN(nextEmissions) ? 0 : nextEmissions

    return {
      totalVotes: totalGaugeVotes,
      bribesPlusFees,
      incomePerVote,
      myVoteValue: myCurrentVoteValue,
      myAvailableVotes: this.selectedVotePower,
      myVoteCount,
      newInclusiveVoteCount,
      newInclusiveIncomePerVote,
      newExpectedIncome,
      incomeEfficiency,
      nextEmissions,
    }
  }

  /**
   * Set vote value (capped at 0-100)
   */
  setVoteValue(value: number): void {
    this.voteValue = Math.min(100, Math.max(0, value))
  }

  // Facade properties for View components - simplify interface access

  /**
   * Unique identifier for this revenue source
   */
  get id(): string {
    return this.gauge.revenueSourceAddress.toString()
  }

  /**
   * Display name of the revenue source (rendered as-is, no transformation)
   */
  get displayName(): string {
    return this.gauge.revenueSource?.displayName ?? "Locating source..."
  }

  /**
   * Protocol/display type of the revenue source
   */
  get protocol(): string {
    return this.gauge.revenueSource?.displayType ?? "Unknown"
  }

  /**
   * APR percentage
   */
  get apr(): number {
    return Number(this.gauge.apr)
  }

  /**
   * Income per vote (simplified name for stats.newInclusiveIncomePerVote)
   */
  get perVote(): number {
    return this.stats.newInclusiveIncomePerVote
  }

  /**
   * Expected income for this allocation (simplified name for stats.newExpectedIncome)
   */
  get expectedIncome(): number {
    return this.stats.newExpectedIncome
  }

  /**
   * Total fees/bribes (simplified name for stats.bribesPlusFees)
   */
  get fees(): number {
    return this.stats.bribesPlusFees
  }

  /**
   * Next emissions
   */
  get emissions(): number {
    return this.stats.nextEmissions
  }

  /**
   * Tokens associated with this revenue source
   */
  get tokens(): Array<TokenStore> {
    return this.gauge.revenueSource?.tokens ?? []
  }

  /**
   * Current vote allocation (alias for voteValue for consistency)
   */
  get allocation(): number {
    return this.voteValue
  }

  /**
   * Total votes on the pool
   */
  get votes(): number {
    return this.gauge.votes
  }

  /**
   * Percentage of total votes
   */
  get votesPercent(): number {
    return this.gauge.votesPercent
  }

  /**
   * Total Value Locked in USD
   * Uses gauge TVL (staked value) only - never falls back to total TVL as it's misleading
   * Revenue comes from the staked portion, not the unstaked liquidity
   * If there's no staked value, returns 0
   */
  get tvlUsd(): number {
    const revenueSource = this.gauge.revenueSource
    if (!revenueSource) return 0

    // Use gauge TVL (staked value) only - never fall back to total TVL as it's misleading
    // If there's no staked value, return 0
    return revenueSource.gaugeValueUSD ?? 0
  }

  /**
   * Dilution Risk: Percentage showing how much the user's vote would dilute the pool
   *
   * Formula: (selectedVotePower / (votes + selectedVotePower)) × 100
   * Represents the percentage by which the user's votes would reduce income per vote
   * if they allocated 100% of their votes to this pool.
   *
   * Example:
   * - Pool has 30,000 votes
   * - User has 30,000 votes selected
   * - Dilution = (30,000 / (30,000 + 30,000)) × 100 = 50%
   * - This means income per vote would drop by 50% if user votes
   */
  get dilutionRisk(): number {
    const votes = this.votes
    const userVotePower = this.selectedVotePower

    // Handle edge cases: if no votes on pool, dilution is 0 (can't dilute nothing)
    if (votes <= 0) {
      return 0
    }

    // If user has no vote power, no dilution risk
    if (userVotePower <= 0) {
      return 0
    }

    // Calculate dilution percentage
    // This represents: if user allocates 100% of their votes, what % of total votes would they be?
    const totalVotesAfterUser = votes + userVotePower
    const dilution = (userVotePower / totalVotesAfterUser) * 100

    // Handle NaN or invalid results
    if (Number.isNaN(dilution) || !Number.isFinite(dilution)) {
      return 0
    }

    // Clamp to 0-100 range (should already be in range, but safety check)
    return Math.max(0, Math.min(100, dilution))
  }

  /**
   * Dilution Risk Rank: Strongly typed risk level based on dilutionRisk percentage
   *
   * Returns one of four risk levels:
   * - "low": 0-24% (green)
   * - "moderate": 25-49% (yellow)
   * - "high": 50-74% (orange)
   * - "critical": 75-100% (red)
   *
   * This rank can be used for filtering, sorting, and UI presentation.
   */
  get dilutionRiskRank(): DilutionRiskRank {
    const risk = this.dilutionRisk

    if (risk <= DILUTION_RISK_THRESHOLDS.LOW_MAX) {
      return "low"
    }
    if (risk <= DILUTION_RISK_THRESHOLDS.MODERATE_MAX) {
      return "moderate"
    }
    if (risk <= DILUTION_RISK_THRESHOLDS.HIGH_MAX) {
      return "high"
    }
    return "critical"
  }

  /**
   * Dilution Risk Color: RGB color value for the risk rank
   * Used for UI presentation (progress bars, filter dropdowns, etc.)
   */
  get dilutionRiskColor(): string {
    return getDilutionRiskColor(this.dilutionRiskRank)
  }

  /**
   * Efficiency: Annualized percentage return based on revenue per epoch relative to gauge TVL
   *
   * Projects current revenue to full epoch based on time elapsed, then calculates annualized efficiency.
   * This ensures early-epoch revenue is properly scaled to reflect its true efficiency.
   *
   * Formula: (projectedRevenue / gaugeTvlUsd) * 52
   * Where projectedRevenue = fees * (epochDuration / timeElapsed)
   *
   * Example:
   * - Gauge has $10,000 TVL (staked value)
   * - 1 hour into epoch, has generated $100 revenue
   * - Projected revenue = $100 * (604800 / 3600) = $16,800
   * - Efficiency = (16800 / 10000) * 52 = 8736% annualized (reflects strong early performance)
   */
  get efficiency(): number {
    const fees = this.fees
    const gaugeTvl = this.tvlUsd // This now uses gaugeValueUSD

    if (fees <= 0 || gaugeTvl <= 0) {
      return 0
    }

    // Calculate time elapsed in current epoch
    const timeElapsed = this.getTimeElapsedInEpoch(this.now)

    // Project current revenue to full epoch
    // If we're 1 hour into a 7-day epoch, multiply by (7 days / 1 hour)
    const projectedRevenue = fees * (EPOCH_DURATION_SECONDS / timeElapsed)

    // Calculate efficiency: (projected revenue per epoch / gauge TVL) * 52 (annualized)
    const efficiency = (projectedRevenue / gaugeTvl) * 52

    if (Number.isNaN(efficiency) || !Number.isFinite(efficiency)) {
      return 0
    }

    return efficiency
  }

  /**
   * Reward tokens/bribes for this pool
   * Exposes the gauge's incentives array
   */
  get incentives(): Array<IncentiveStore> {
    return this.gauge.incentives
  }

  /**
   * Last epoch vote count for this gauge from selected locks
   * Used for displaying "X last epoch" in the table view
   */
  get lastEpochVoteCount(): number {
    if (!this.lockLookup) {
      return 0
    }

    // Query locks for last epoch votes
    const selectedLockIds = this.selectedLockIds
    const selectedLocks = selectedLockIds
      .map((id) => this.lockLookup!.find(id))
      .filter((lock): lock is NonNullable<typeof lock> => lock !== undefined)

    // Sum up last epoch votes for this gauge from all selected locks
    const totalLastEpochVotes = selectedLocks.reduce((sum, lock) => {
      const vote = lock.getLastEpochVoteForGauge(this.gauge.revenueSourceAddress)
      return sum + (vote?.balance ?? 0)
    }, 0)

    return sanitizeNumber(totalLastEpochVotes)
  }
}
